import { useMemo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BillingState,
  GetProgramEnrollmentPaymentPlansQuery,
  PaymentPlanStatus,
} from '@sandtech-hub-monorepo/ehub-client';

import { Button, cn } from '@sandtech-hub-monorepo/ui-kit';
import { AlertCircle, CheckCircle, ChevronRight, Info } from 'lucide-react';
import { differenceInDays, differenceInHours } from 'date-fns';
import { ExternalApplicationLinks } from '../../../utils';
import { formatDate } from '../../dateFormat';

type AlertLevel = 'blue' | 'amber' | 'red' | 'success' | null;

// Context passed to message generator functions
type PaymentMessageContext = {
  formattedDate: string;
  nextAmount: number;
  isAllAccess: boolean;
};

type MessageGenerator = (ctx: PaymentMessageContext) => ReactNode;

// ponytail: verbatim restore of the pre-PEDS-16058 (pre-2026-08-17) banner
// logic. PEDS-16058 changed banner outcomes here; FitProgramEnrollmentPaymentBanner
// carries that newer logic for its own consumer. Upgrade path: once the
// newer logic is fixed for ALX too, delete this file and switch
// ProgramEnrollmentsView/index.tsx to FitProgramEnrollmentPaymentBanner.
const MESSAGES: {
  success: ReactNode;
  fallback: { red: ReactNode; amber: ReactNode; blue: ReactNode };
  beforeFirst: {
    blue: MessageGenerator;
    amber: MessageGenerator;
    red: MessageGenerator;
  };
} = {
  success: (
    <>
      <b>Payment up to date.</b> Your fees are up to date, ensuring
      uninterrupted access to your course. Keep it up!
    </>
  ),
  fallback: {
    red: (
      <>
        <b>Payment required.</b> Please make a payment.
      </>
    ),
    amber: (
      <>
        <b>Payment due soon.</b> Please make a payment.
      </>
    ),
    blue: (
      <>
        <b>Payment coming up.</b> Please make a payment.
      </>
    ),
  },
  beforeFirst: {
    blue: ({ formattedDate, isAllAccess }) => (
      <>
        <b>Payment coming up:</b> Make your first payment by{' '}
        <b>{formattedDate}</b> to ensure uninterrupted learning
        {!isAllAccess && ' and begin the term'}.
      </>
    ),
    amber: () => (
      <>
        <b>Payment due:</b> Complete your first payment to unlock All Access.
      </>
    ),
    red: ({ nextAmount }) => (
      <>
        <b>Payment overdue:</b> Payment of <b>${nextAmount.toFixed(2)}</b> is
        now overdue, please make a payment urgently to avoid disruption to
        your learning.
      </>
    ),
  },
};

interface Props {
  paymentPlan?: GetProgramEnrollmentPaymentPlansQuery['viewer']['paymentPlans'][number];
  paymentFAQsLink: string;
  showPaymentPlanName?: boolean;
  showFAQLink?: boolean;
}

export const AlxProgramEnrollmentPaymentBanner = ({
  paymentPlan,
  paymentFAQsLink,
  showPaymentPlanName = true,
  showFAQLink = true,
}: Props) => {
  const navigate = useNavigate();

  const { alertLevel, message, showBanner } = useMemo(() => {
    if (!paymentPlan) {
      return { showBanner: false };
    }
    if (
      !paymentPlan.status ||
      paymentPlan.status === PaymentPlanStatus.Cancelled
    ) {
      return { showBanner: false };
    }

    const {
      billingState,
      totalPaidAmount = 0,
      nextInstalmentDate,
      amountPerInstalment,
    } = paymentPlan;
    const isAllAccess = paymentPlan.code === 'all-access';

    // If payment is completed or up to date, show success banner
    if (
      [
        BillingState.UpToDate,
        BillingState.InAdvance,
        BillingState.Completed,
      ].includes(billingState)
    ) {
      return {
        alertLevel: 'success' as AlertLevel,
        message: MESSAGES.success,
        showBanner: true,
      };
    }

    // Calculate due date from the plan's next instalment date, since the
    // payment service doesn't always maintain a next invoice to key off.
    const dueDate = nextInstalmentDate ? new Date(nextInstalmentDate) : null;
    if (!dueDate) {
      // Fallback to old logic if no due date
      const isError = [
        BillingState.InArrears,
        BillingState.Inactive,
        BillingState.Grace,
        BillingState.AtRisk,
        BillingState.Pending,
        BillingState.AwaitingPaymentConfirmation,
      ].includes(billingState);

      const level = isError ? 'red' : 'amber';
      return {
        alertLevel: level as AlertLevel,
        message: MESSAGES.fallback[level as 'red' | 'amber'],
        showBanner: true,
      };
    }

    const now = new Date();
    const daysUntilDue = differenceInDays(dueDate, now);
    const hoursUntilDue = differenceInHours(dueDate, now);

    // Determine alert level based on time until due
    let level: AlertLevel = null;
    if (daysUntilDue < 0) {
      level = 'red'; // Overdue
    } else if (hoursUntilDue < 48) {
      level = 'amber'; // Due soon (<48 hours)
    } else if (daysUntilDue <= 10) {
      level = 'blue'; // Coming up (10 days before)
    } else if (isAllAccess) {
      level = 'blue'; // Always show all-access banner
    } else {
      // Don't show banner yet
      return { showBanner: false };
    }

    // Convert cents to dollars
    const totalPaid = totalPaidAmount / 100;
    const nextAmount = (amountPerInstalment || 0) / 100;

    // Has the first $5 instalment been paid yet? Anything past that first
    // payment falls through to the generic fallback message below.
    const isFirstPayment = totalPaid < 5;

    const formattedDate = formatDate(dueDate);
    const ctx: PaymentMessageContext = {
      formattedDate,
      nextAmount,
      isAllAccess,
    };

    const bannerMessage: ReactNode = isFirstPayment
      ? MESSAGES.beforeFirst[level as 'blue' | 'amber' | 'red'](ctx)
      : MESSAGES.fallback[level as 'blue' | 'amber' | 'red'];

    return {
      alertLevel: level,
      message: bannerMessage,
      showBanner: true,
    };
  }, [paymentPlan]);

  if (!showBanner) {
    return null;
  }

  const isError = alertLevel === 'red';
  const isSuccess = alertLevel === 'success';
  const isInfo = alertLevel === 'blue';
  const isWarning = alertLevel === 'amber';

  return (
    <div
      className={cn(
        'px-4 py-3 lg:py-1 flex flex-col sm:flex-row gap-2 lg:gap-4 items-center border w-full rounded-lg',
        isError ? 'border-border-error bg-destructive/10 text-destructive' : '',
        isSuccess ? 'border-success bg-success/10 text-success' : '',
        isInfo ? 'border-info bg-info/10 text-info' : '',
        isWarning ? 'border-warning bg-warning/10 text-warning' : ''
      )}
    >
      {isError && <AlertCircle className="stroke-destructive h-6 w-6" />}
      {isSuccess && <CheckCircle className="stroke-success min-h-6 min-w-6" />}
      {(isInfo || isWarning) && (
        <Info className="stroke-current min-h-6 min-w-6" />
      )}
      {showPaymentPlanName && <b>{paymentPlan?.definition?.name}</b>}
      <div className="text-sm">
        {message}{' '}
        {showFAQLink && !isSuccess && paymentPlan && (
          <span>
            Read our{' '}
            <Button
              className={`h-fit p-0 font-semibold underline ${
                isError
                  ? 'text-error'
                  : isWarning
                  ? 'text-warning'
                  : 'text-primary'
              }`}
              variant={'link'}
              onClick={() => navigate(paymentFAQsLink)}
            >
              payments FAQ
            </Button>
            .
          </span>
        )}
      </div>
      {paymentPlan?.billingState !== BillingState.Completed && (
        <Button
          variant={
            isError
              ? 'ghostDestructive'
              : isInfo
              ? 'ghostInfo'
              : isWarning
              ? 'ghostWarning'
              : 'ghostSuccess'
          }
          className="mx-auto sm:ml-auto sm:mr-0 p-0"
          onClick={() =>
            window.location.replace(
              paymentPlan?.viewUrl || ExternalApplicationLinks.payments
            )
          }
        >
          {isError && (paymentPlan ? 'Pay now' : 'Activate')}
          {isSuccess && 'Make a Payment'}
          {(isInfo || isWarning) && 'Make payment now'}
          <ChevronRight />
        </Button>
      )}
    </div>
  );
};
